import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";

const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const faqs = [
    {
      question: '¿Cómo funciona el "clon de IA" de mi estilo?',
      answer: 'Durante el onboarding, subes ejemplos de tus mensajes y defines tu tono preferido (formal, balanceado o casual). La IA analiza tus patrones de comunicación y replica tu forma de hablar. Puedes hacer una vista previa y ajustar hasta que suene exactamente como tú.',
    },
    {
      question: '¿Mis pacientes sabrán que es una IA?',
      answer: 'Tú decides el nivel de transparencia. Recomendamos ser honestos: el mensaje inicial aclara que es tu asistente IA personal. La mayoría de pacientes lo ven como un valor agregado, no una desventaja.',
    },
    {
      question: '¿Qué pasa si la IA da una recomendación incorrecta?',
      answer: 'La IA solo trabaja dentro de los parámetros del plan que tú defines. No inventa dietas ni da consejos médicos por su cuenta. Si detecta una situación fuera de su alcance, te notifica y sugiere al paciente contactarte directamente.',
    },
    {
      question: '¿Puedo ver las conversaciones de mis pacientes?',
      answer: 'Sí, tienes acceso completo al historial de conversaciones. Además, recibes alertas cuando un paciente reporta dificultades o cuando la IA detecta patrones que requieren tu atención.',
    },
    {
      question: '¿Funciona solo con WhatsApp?',
      answer: 'Por ahora sí. WhatsApp es el canal más usado y confiable en Latinoamérica. Es donde tus pacientes ya están cómodos. En el futuro, expandiremos a otros canales según la demanda de los fundadores.',
    },
    {
      question: '¿Qué pasa si quiero cancelar?',
      answer: 'Puedes cancelar en cualquier momento sin penalidad. Ofrecemos 30 días de garantía completa: si no ves resultados, te devolvemos el 100% de tu inversión.',
    },
    {
      question: '¿Necesito conocimientos técnicos?',
      answer: 'Cero. La configuración es tan simple como llenar un formulario y subir algunos documentos. Todo el proceso guiado toma menos de 10 minutos. Además, los fundadores reciben un onboarding 1:1 personalizado.',
    },
    {
      question: '¿Qué incluye el precio de fundador?',
      answer: 'El precio de $29/mes incluye todo: hasta 50 pacientes, mensajes ilimitados, todas las funciones de IA, y los beneficios exclusivos de fundador (precio bloqueado de por vida, onboarding 1:1, grupo privado y voto en roadmap).',
    },
  ];

  return (
    <section id="faq" ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            Preguntas frecuentes
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight">
            Todo lo que necesitas saber
          </h2>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-[#2E5C41]/10 rounded-2xl px-6 data-[state=open]:border-[#2E5C41]/30 transition-colors"
                data-testid={`faq-accordion-${index}`}
              >
                <AccordionTrigger className="text-left font-cabinet font-medium text-[#1A2E24] hover:text-[#2E5C41] py-5 [&[data-state=open]>svg]:rotate-180">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#4B5E53] text-sm leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Contact note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-[#4B5E53] text-sm">
            ¿Tienes otra pregunta? Escríbenos a{' '}
            <a href="mailto:hola@habitia.app" className="text-[#2E5C41] font-medium hover:underline">
              hola@habitia.app
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
